// src/utils/reporteProductosGenerator.js

// 👈 NUEVO: Importar función que obtiene los datos de la empresa actual
import { obtenerDatosEmpresa } from './tenantConfig';

// Configuración por defecto (fallback)
export const SYSTEM_JJ_CONFIG = {
  nombre: "SYSTEM JJ",
  rif: "J-41023119-0",
  direccion: "C.C. LATIN CENTER PB LOCAL 01",
  telefono: "0416-7594723",
  whatsapp: "04167594723",
  ciudad: "ACARIGUA - PORTUGUESA",
  email: "soporte@systemjj.com",
  sitioWeb: "www.systemjj.com",
  slogan: "Servicio Técnico Especializado",
  lema: "Tecnología al servicio de la gente"
};

/**
 * 👈 NUEVO: Obtiene la config de la empresa actual con fallback
 */
const getEmpresa = () => {
  try {
    const datos = obtenerDatosEmpresa();
    return {
      nombre: datos.nombreComercial || datos.nombre || SYSTEM_JJ_CONFIG.nombre,
      rif: datos.rif || SYSTEM_JJ_CONFIG.rif,
      direccion: datos.direccion || SYSTEM_JJ_CONFIG.direccion,
      telefono: datos.telefono1 || datos.telefono2 || SYSTEM_JJ_CONFIG.telefono,
      whatsapp: datos.whatsapp || datos.telefono1 || SYSTEM_JJ_CONFIG.whatsapp,
      ciudad: datos.ciudad || SYSTEM_JJ_CONFIG.ciudad,
      email: datos.email || SYSTEM_JJ_CONFIG.email,
      sitioWeb: datos.sitioWeb || SYSTEM_JJ_CONFIG.sitioWeb,
      slogan: datos.slogan || SYSTEM_JJ_CONFIG.slogan,
      lema: datos.slogan || SYSTEM_JJ_CONFIG.lema,
      logoBase64: datos.logoBase64 || null,
    };
  } catch (err) {
    console.warn('⚠️ No se pudo obtener config del tenant, usando fallback:', err);
    return { ...SYSTEM_JJ_CONFIG, logoBase64: null };
  }
};

// Variable para el logo en base64
let LOGO_BASE64 = '';

/**
 * 👈 MODIFICADO: Prioriza el logo del tenant; si no hay, usa /jj.png
 */
export const cargarLogo = async () => {
  try {
    // Primero intentar con el logo del tenant
    const empresa = getEmpresa();
    if (empresa.logoBase64) {
      const base64Limpio = empresa.logoBase64.includes(',')
        ? empresa.logoBase64.split(',')[1]
        : empresa.logoBase64;
      LOGO_BASE64 = base64Limpio;
      console.log('✅ Logo del tenant cargado (productos)');
      return LOGO_BASE64;
    }

    // Fallback al logo por defecto
    const response = await fetch('/jj.png');
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        LOGO_BASE64 = reader.result.split(',')[1];
        resolve(LOGO_BASE64);
      };
      reader.onerror = () => {
        LOGO_BASE64 = '';
        resolve('');
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error cargando el logo:', error);
    LOGO_BASE64 = '';
    return '';
  }
};

export const getLogoBase64 = () => LOGO_BASE64;

// ==================== UTILIDADES DE FORMATO ====================

export const formatearFechaReporte = (fechaISO) => {
  if (!fechaISO) return 'No disponible';
  try {
    const fecha = new Date(fechaISO);
    return fecha.toLocaleDateString('es-VE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return fechaISO;
  }
};

export const formatearFechaCorta = (fechaISO) => {
  if (!fechaISO) return 'No disponible';
  try {
    const fecha = new Date(fechaISO);
    return fecha.toLocaleDateString('es-VE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return fechaISO;
  }
};

export const formatearNumero = (numero, decimales = 2) => {
  if (numero === null || numero === undefined) return '0.00';
  return new Intl.NumberFormat('es-VE', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales
  }).format(numero);
};

export const formatearMonedaUSD = (monto) => {
  if (monto === null || monto === undefined) return '$0.00';
  return `$${formatearNumero(monto, 2)}`;
};

export const convertirEstadoTexto = (estado) => {
  if (!estado) return 'DESCONOCIDO';
  const estadoLower = estado.toString().toLowerCase();
  if (estadoLower === 'activo' || estadoLower === 'true' || estadoLower === '1') return 'ACTIVO';
  if (estadoLower === 'inactivo' || estadoLower === 'false' || estadoLower === '0') return 'INACTIVO';
  return estado.toString().toUpperCase();
};

export const getBadgeEstado = (estado) => {
  const estadoTexto = convertirEstadoTexto(estado);
  return {
    texto: estadoTexto,
    clase: estadoTexto === 'ACTIVO' ? 'estado-activo' : 'estado-inactivo'
  };
};

export const getStockClass = (stock, stockMinimo) => {
  const stockNum = Number(stock) || 0;
  const stockMinimoNum = Number(stockMinimo) || 0;

  if (stockNum <= 0) return 'stock-cero';
  if (stockMinimoNum > 0 && stockNum <= stockMinimoNum) return 'stock-bajo';
  return 'stock-normal';
};

export const getStockTexto = (stock, stockMinimo) => {
  const stockNum = Number(stock) || 0;
  const stockMinimoNum = Number(stockMinimo) || 0;

  if (stockNum <= 0) return 'SIN STOCK';
  if (stockMinimoNum > 0 && stockNum <= stockMinimoNum) return 'STOCK BAJO';
  return 'NORMAL';
};

// ==================== CÁLCULOS ====================

export const ordenarProductosPorNombre = (productos) => {
  if (!productos || productos.length === 0) return productos;
  return [...productos].sort((a, b) => {
    const nombreA = (a.nombre || '').toLowerCase();
    const nombreB = (b.nombre || '').toLowerCase();
    return nombreA.localeCompare(nombreB, 'es');
  });
};

export const calcularEstadisticasReporte = (productos, tasas = { usdt: null, bcv: null }) => {
  const total = productos.length;
  const activos = productos.filter(p =>
    p.estado?.toLowerCase() === 'activo' || p.estado === true || p.estado === 1
  ).length;

  const stockTotal = productos.reduce((sum, p) => sum + (Number(p.stock) || 0), 0);
  const productosSinStock = productos.filter(p => !p.stock || p.stock === 0).length;
  const valorInventarioUSD = productos.reduce((sum, p) =>
    sum + ((Number(p.precio_compra_usdt) || 0) * (Number(p.stock) || 0)), 0);
  const valorPotencialVentaUSD = productos.reduce((sum, p) =>
    sum + ((Number(p.precio_venta_usd) || 0) * (Number(p.stock) || 0)), 0);

  const sumaPreciosCompra = productos.reduce((sum, p) => sum + (Number(p.precio_compra_usdt) || 0), 0);
  const sumaPreciosVenta = productos.reduce((sum, p) => sum + (Number(p.precio_venta_usd) || 0), 0);

  const precioPromedioCompra = total > 0 ? sumaPreciosCompra / total : 0;
  const precioPromedioVenta = total > 0 ? sumaPreciosVenta / total : 0;

  const categoriasMap = {};
  productos.forEach(producto => {
    const catNombre = producto.nombreCategoria || 'Sin Categoría';
    if (!categoriasMap[catNombre]) {
      categoriasMap[catNombre] = { nombre: catNombre, total: 0, stockTotal: 0 };
    }
    categoriasMap[catNombre].total++;
    categoriasMap[catNombre].stockTotal += Number(producto.stock) || 0;
  });

  return {
    total,
    activos,
    inactivos: total - activos,
    stockTotal,
    productosSinStock,
    valorInventarioUSD,
    valorPotencialVentaUSD,
    gananciaPotencial: valorPotencialVentaUSD - valorInventarioUSD,
    precioPromedioCompra,
    precioPromedioVenta,
    categorias: Object.values(categoriasMap).sort((a, b) => b.total - a.total)
  };
};

export const filtrarProductosParaReporte = (productos, filtro) => {
  let productosFiltrados = [...productos];

  switch (filtro) {
    case 'todos': break;
    case 'activos':
      productosFiltrados = productosFiltrados.filter(p =>
        p.estado?.toLowerCase() === 'activo' || p.estado === true || p.estado === 1
      );
      break;
    case 'inactivos':
      productosFiltrados = productosFiltrados.filter(p =>
        p.estado?.toLowerCase() === 'inactivo' || p.estado === false || p.estado === 0
      );
      break;
    case 'stock_cero':
      productosFiltrados = productosFiltrados.filter(p => (Number(p.stock) || 0) === 0);
      break;
    case 'stock_bajo':
      productosFiltrados = productosFiltrados.filter(p => {
        const stockNum = Number(p.stock) || 0;
        const stockMinimoNum = Number(p.stock_minimo) || 0;
        return stockNum > 0 && stockMinimoNum > 0 && stockNum <= stockMinimoNum;
      });
      break;
    case 'con_stock':
      productosFiltrados = productosFiltrados.filter(p => (p.stock || 0) > 0);
      break;
    default:
      if (!isNaN(filtro) && filtro > 0) {
        productosFiltrados = productosFiltrados.filter(p => p.idCategoria === filtro);
      }
      break;
  }

  return ordenarProductosPorNombre(productosFiltrados);
};

// ==================== GENERAR HTML ====================

export const generarHTMLReporteProductos = (productos, titulo = null, filtro = 'todos', tasas = { usdt: null, bcv: null }) => {
  const empresa = getEmpresa();   // 👈 NUEVO: datos dinámicos del tenant
  const fechaActual = new Date();

  const productosOrdenados = ordenarProductosPorNombre(productos);
  const estadisticas = calcularEstadisticasReporte(productosOrdenados, tasas);

  let tituloReal = titulo;
  if (!tituloReal) {
    const titulosPorFiltro = {
      'todos': 'REPORTE GENERAL DE PRODUCTOS',
      'activos': 'PRODUCTOS ACTIVOS',
      'inactivos': 'PRODUCTOS INACTIVOS',
      'stock_cero': 'PRODUCTOS SIN STOCK',
      'stock_bajo': 'PRODUCTOS CON STOCK BAJO',
      'con_stock': 'PRODUCTOS CON STOCK DISPONIBLE'
    };
    tituloReal = titulosPorFiltro[filtro] || 'REPORTE DE PRODUCTOS';
  }

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>${tituloReal} - ${empresa.nombre}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Arial', 'Helvetica', sans-serif;
      background: white;
      padding: 15px;
    }
    .reporte { max-width: 1200px; margin: 0 auto; }
    
    .header {
      padding: 10px 0;
      border-bottom: 1px solid #000;
      margin-bottom: 15px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .logo-area { display: flex; align-items: center; gap: 10px; }
    .logo-img { height: 45px; width: auto; object-fit: contain; }
    .empresa-nombre { font-size: 16px; font-weight: bold; }
    .empresa-slogan { font-size: 9px; color: #666; }
    .rif-info { text-align: right; font-size: 9px; line-height: 1.3; }
    
    .titulo { padding: 8px 0; }
    .titulo h1 { font-size: 16px; font-weight: bold; }
    .fecha { font-size: 9px; color: #666; margin-top: 3px; }
    
    .stats-bar {
      background: #f8f8f8;
      padding: 6px 12px;
      margin: 10px 0;
      border: 1px solid #e0e0e0;
      font-size: 11px;
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 15px;
    }
    .stat-item-compact {
      display: inline-flex;
      align-items: baseline;
      gap: 4px;
    }
    .stat-label-compact {
      font-weight: normal;
      color: #666;
    }
    .stat-valor-compact {
      font-weight: bold;
      color: #000;
    }
    
    .tabla-container { overflow-x: auto; }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 9px;
      border: 1px solid #ddd;
    }
    th {
      background: #f5f5f5;
      padding: 6px 4px;
      text-align: left;
      font-weight: bold;
      border-bottom: 1px solid #ddd;
    }
    td {
      padding: 5px 4px;
      border-bottom: 1px solid #eee;
    }
    tr:hover td { background: #fafafa; }
    .text-right { text-align: right; }
    
    .controles {
      text-align: center;
      margin-top: 15px;
      padding: 10px;
      background: #f5f5f5;
    }
    button {
      padding: 6px 15px;
      font-size: 10px;
      margin: 0 3px;
      cursor: pointer;
      border: 1px solid #ccc;
      background: white;
    }
    button:hover { background: #f0f0f0; }
    
    @media print {
      body { padding: 0; }
      .controles { display: none; }
      @page { margin: 1cm; size: landscape; }
    }
  </style>
</head>
<body>
<div class="reporte">
  <div class="header">
    <div class="logo-area">
      ${getLogoBase64() ? `<img src="data:image/jpeg;base64,${getLogoBase64()}" class="logo-img" onerror="this.style.display='none'">` : ''}
      <div>
        <div class="empresa-nombre">${empresa.nombre}</div>
        <div class="empresa-slogan">${empresa.slogan}</div>
      </div>
    </div>
    <div class="rif-info">
      RIF: ${empresa.rif}<br>
      ${empresa.ciudad}
    </div>
  </div>
  
  <div class="titulo">
    <h1>${tituloReal}</h1>
    <div class="fecha">${formatearFechaReporte(fechaActual.toISOString())}</div>
  </div>
  
  <div class="stats-bar">
    <div class="stat-item-compact"><span class="stat-label-compact">Total:</span><span class="stat-valor-compact">${estadisticas.total}</span></div>
    <div class="stat-item-compact"><span class="stat-label-compact">Activos:</span><span class="stat-valor-compact">${estadisticas.activos}</span></div>
    <div class="stat-item-compact"><span class="stat-label-compact">Sin Stock:</span><span class="stat-valor-compact">${estadisticas.productosSinStock}</span></div>
    <div class="stat-item-compact"><span class="stat-label-compact">Inventario:</span><span class="stat-valor-compact">${formatearMonedaUSD(estadisticas.valorInventarioUSD)}</span></div>
  </div>
  
  <div class="tabla-container">
    ${productosOrdenados.length > 0 ? `
      <table>
      <thead>
        <tr>
          <th>Código</th>
          <th>Nombre</th>
          <th>Categoría</th>
          <th>Marca</th>
          <th class="text-right">Stock</th>
          <th class="text-right">Compra ($)</th>
          <th class="text-right">Venta ($)</th>
        </tr>
      </thead>
      <tbody>
        ${productosOrdenados.map(p => `
          <tr>
            <td><strong>${p.codigo || '—'}</strong></td>
            <td>${p.nombre || '—'}</td>
            <td>${p.nombreCategoria || '—'}</td>
            <td>${p.marca || '—'}</td>
            <td class="text-right"><strong>${p.stock || 0}</strong></td>
            <td class="text-right">${formatearMonedaUSD(p.precio_compra_usdt)}</td>
            <td class="text-right">${formatearMonedaUSD(p.precio_venta_usd)}</td>
          </tr>
        `).join('')}
      </tbody>
      </table>
    ` : '<div style="padding: 30px; text-align: center; border: 1px solid #ddd;">No hay productos para mostrar</div>'}
  </div>
</div>

<div class="controles">
  <button onclick="window.print()">🖨️ IMPRIMIR</button>
  <button onclick="window.close()">✖️ CERRAR</button>
</div>
</body>
</html>`;
};

// ==================== IMPRIMIR ====================

export const imprimirReporteProductos = async (productos, titulo = null, filtro = 'todos', tasas = { usdt: null, bcv: null }) => {
  if (!productos || productos.length === 0) {
    alert('No hay productos para mostrar');
    return false;
  }

  try {
    // Resetear logo para que se recargue el del tenant actual
    LOGO_BASE64 = '';
    await cargarLogo();

    const htmlReporte = generarHTMLReporteProductos(productos, titulo, filtro, tasas);
    const ventana = window.open('', '_blank', 'width=1100,height=700,menubar=yes,toolbar=yes,scrollbars=yes');

    if (!ventana) {
      alert('Por favor, permite ventanas emergentes');
      return false;
    }

    ventana.document.write(htmlReporte);
    ventana.document.close();
    ventana.focus();
    return true;
  } catch (error) {
    console.error('Error:', error);
    alert('Error al generar el reporte');
    return false;
  }
};

// ==================== EXCEL ====================

export const exportarProductosExcel = (productos, titulo = 'productos', filtro = 'todos') => {
  try {
    if (typeof window === 'undefined') return false;

    const productosOrdenados = ordenarProductosPorNombre(productos);

    const XLSX = require('xlsx');
    const wb = XLSX.utils.book_new();

    const data = productosOrdenados.map(p => ({
      'Código': p.codigo || '',
      'Nombre': p.nombre || '',
      'Categoría': p.nombreCategoria || '',
      'Marca': p.marca || '',
      'Stock': p.stock || 0,
      'Stock Mínimo': p.stock_minimo || 0,
      'Precio Compra ($)': Number(p.precio_compra_usdt) || 0,
      'Precio Venta ($)': Number(p.precio_venta_usd) || 0
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = [{ wch: 15 }, { wch: 35 }, { wch: 20 }, { wch: 15 }, { wch: 10 }, { wch: 12 }, { wch: 15 }, { wch: 15 }];

    XLSX.utils.book_append_sheet(wb, ws, 'Productos');

    const fecha = new Date().toISOString().split('T')[0];
    const nombreArchivo = `${titulo}_${fecha}.xlsx`;
    XLSX.writeFile(wb, nombreArchivo);

    return { success: true, filename: nombreArchivo, count: productosOrdenados.length };
  } catch (error) {
    console.error('Error exportando Excel:', error);
    return { success: false, error: error.message };
  }
};

// ==================== PDF ====================

export const exportarProductosPDF = (productos, title = 'Reporte de Productos', filtro = 'todos') => {
  try {
    if (typeof window === 'undefined') return false;

    const empresa = getEmpresa();   // 👈 NUEVO: datos dinámicos del tenant
    const productosOrdenados = ordenarProductosPorNombre(productos);

    const { jsPDF } = require('jspdf');
    require('jspdf-autotable');

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.width;

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`${title} - ${empresa.nombre}`, pageWidth / 2, 12, { align: 'center' });

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`RIF: ${empresa.rif} | ${empresa.ciudad}`, pageWidth / 2, 17, { align: 'center' });
    doc.text(`Generado: ${new Date().toLocaleDateString()}`, pageWidth / 2, 21, { align: 'center' });

    const total = productosOrdenados.length;
    const conStock = productosOrdenados.filter(p => (p.stock || 0) > 0).length;
    const valorInventario = productosOrdenados.reduce((sum, p) => sum + ((Number(p.precio_compra_usdt) || 0) * (Number(p.stock) || 0)), 0);

    doc.setFontSize(7);
    doc.text(`Total: ${total} | Activos: ${productosOrdenados.filter(p => p.estado?.toLowerCase() === 'activo').length} | Sin Stock: ${total - conStock} | Inventario: $${formatearNumero(valorInventario, 2)}`, 14, 26);

    const tableData = productosOrdenados.map(p => [
      p.codigo || '',
      (p.nombre || '').substring(0, 30),
      p.nombreCategoria || '',
      p.marca || '',
      p.stock || 0,
      formatearMonedaUSD(p.precio_compra_usdt),
      formatearMonedaUSD(p.precio_venta_usd)
    ]);

    doc.autoTable({
      startY: 30,
      head: [['Código', 'Nombre', 'Categoría', 'Marca', 'Stock', 'Compra', 'Venta']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [100, 100, 100], textColor: 255, fontSize: 7 },
      styles: { fontSize: 6, cellPadding: 1.5 },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 45 },
        2: { cellWidth: 30 },
        3: { cellWidth: 25 },
        4: { cellWidth: 12 },
        5: { cellWidth: 18 },
        6: { cellWidth: 18 }
      },
      margin: { top: 30 },
      didDrawPage: function (data) {
        doc.setFontSize(6);
        doc.text(`Página ${data.pageNumber}`, pageWidth / 2, doc.internal.pageSize.height - 8, { align: 'center' });
      }
    });

    const nombreArchivo = `reporte_productos_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(nombreArchivo);

    return { success: true, filename: nombreArchivo, count: productosOrdenados.length };
  } catch (error) {
    console.error('Error exportando PDF:', error);
    return { success: false, error: error.message };
  }
};

export default {
  SYSTEM_JJ_CONFIG,
  cargarLogo,
  getLogoBase64,
  formatearFechaReporte,
  formatearFechaCorta,
  formatearNumero,
  formatearMonedaUSD,
  convertirEstadoTexto,
  getBadgeEstado,
  getStockClass,
  getStockTexto,
  ordenarProductosPorNombre,
  calcularEstadisticasReporte,
  filtrarProductosParaReporte,
  generarHTMLReporteProductos,
  imprimirReporteProductos,
  exportarProductosExcel,
  exportarProductosPDF
};